import axios from 'axios';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdRemoveRedEye, MdDelete, MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { Tooltip } from 'react-tooltip';
import { RiPencilFill } from 'react-icons/ri';
import ReactPaginate from 'react-paginate';
import { ToastContainer, toast } from 'react-toastify';
import { FaRegSquareCheck } from "react-icons/fa6";
import { CgCloseR } from "react-icons/cg";

import constant from '../../../utils/Constant';
import config from '../../../config/config';
import Loader from '../../partials/Loader';

const {
    BOOLEAN_FALSE: falseValue,
    BOOLEAN_TRUE: trueValue,
    EMPTY_STRING: emtpyString,
    DELETE_MOBILE_QUOTATION_END_POINT,
    MOBILE_BAR_QUOTATION_END_POINT,
    QUOTATION_STATUS_END_POINT,
    REQUESTED_RECORD,
    ZERO,
    TWO,
    ONE,
    STATUS_200,
    STATUS_401,
    LOGOUT,
    THREE
} = constant;

const AllQuotation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPage = location.state?.page || ONE;
    const modalRef = useRef();

    const [data, setData] = useState([]);
    const [status, setStatus] = useState({});
    const [page, setPage] = useState(currentPage);
    const [totalPages, setTotalPages] = useState(ONE);
    const [vendorStat, setVendorStat] = useState(THREE);
    const [mobileID, setMobileID] = useState(emtpyString);
    const [confirmationModal, setConfirmationModal] = useState(falseValue);
    const [showConfirmation, setShowConfirmation] = useState(falseValue);
    const [disableButton, setDisableButton] = useState(falseValue);
    const [loading, setLoading] = useState(falseValue);
    const [disable, setDisable] = useState(falseValue);

    // * get token from local storage
    const token = localStorage.getItem('vendor_super_token');

    // * execute mobile quote request api
    const executeMobileQuoteApi = useCallback(async () => {
        try {
            const apiUrl = `${config.appBaseUrl + MOBILE_BAR_QUOTATION_END_POINT}/${vendorStat}?page=${page}`;

            // * execute get request
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // * api response
            const result = response.data;

            // * if response is success
            if (result.code === STATUS_200 && result.status) {
                setData(result.data.data);
                setPage(page);
                setTotalPages(result.data.last_page);
            }
        } catch (error) {
            // * if token is expired
            if (error?.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
            toast.error(error);
        }
    }, [navigate, token, page, vendorStat]);

    // * execute mobile quote request api
    useEffect(() => {
        executeMobileQuoteApi();
    }, [executeMobileQuoteApi, token, page, vendorStat]);

    // * handle page change
    const handlePageChange = (selectedPage) => {
        const newPage = selectedPage.selected + ONE;
        setPage(newPage);
        navigate('/quotation/mobile-bar-quotation', { state: { page: newPage } });
    };

    // * execute mobile quote delete
    const executeDeleteMobileApi = async () => {

        setLoading(trueValue);
        setDisable(trueValue);

        try {
            const apiUrl = `${config.appBaseUrl + DELETE_MOBILE_QUOTATION_END_POINT}/${mobileID}`;

            //* post request
            const response = await axios.get(apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeMobileQuoteApi();
                setConfirmationModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    //* handle click outside
    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            setConfirmationModal(falseValue);
            setShowConfirmation(falseValue);
        }
    };

    //* handle click outside
    useEffect(() => {
        if (confirmationModal || showConfirmation) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [confirmationModal, showConfirmation]);

    // * handle modal open and close
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Tab' && (confirmationModal || showConfirmation)) {
                const focusableElements = document.querySelectorAll(
                    '.modal input, .modal button, .modal select, .modal textarea, .modal a'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                //* prevent tabbing outside the modal
                if (event.shiftKey) {
                    //* shift + tab (focus previous element)
                    if (document.activeElement === firstElement) {
                        lastElement.focus();  //* loop to the last element
                        event.preventDefault();
                    }
                } else {
                    //* tab (focus next element)
                    if (document.activeElement === lastElement) {
                        firstElement.focus();  //* loop to the first element
                        event.preventDefault();
                    }
                }
            }
        };

        //* check when modal is open
        if (confirmationModal || showConfirmation) {
            document.body.classList.add('modal-open');
            document.addEventListener('keydown', handleKeyDown);
            //* focus the first element inside the modal when it opens
            const firstFocusable = document.querySelector('.modal button, .modal input, .modal select, .modal a');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        } else {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [confirmationModal, showConfirmation]);

    // * show confirmation model
    const showConfirmationModel = (params) => {
        const { id, status } = params;
        setStatus({
            id: id,
            status: status
        })
        setShowConfirmation(trueValue);
    }

    // * execute quotation status api
    const executeQuotationStatusApi = async () => {
        setLoading(trueValue)
        setDisableButton(trueValue)

        const sendRequest = {
            id: status.id,
            status: status.status,
            type: ONE
        }

        try {
            const apiUrl = config.appBaseUrl + QUOTATION_STATUS_END_POINT;

            // * execute patch request
            const response = await axios.post(apiUrl, sendRequest,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // * if response is success then set data
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                executeMobileQuoteApi();
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            // * if token is expired
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
            toast.error(error);
        } finally {
            setLoading(falseValue);
            setDisableButton(falseValue);
        }
        setShowConfirmation(falseValue);
    };

    return (
        <>
            <ToastContainer autoClose={700} position='top-center' />

            {/* //* status update model */}
            {showConfirmation && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={modalRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to update status?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setShowConfirmation(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeQuotationStatusApi}
                                        disabled={disableButton}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* //* delete service confirmation model */}
            {confirmationModal && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={modalRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to delete this quotation?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setConfirmationModal(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeDeleteMobileApi}
                                        disabled={disable}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className='page-content'>
                <div className='container-fluid'>
                    {/* //* start page title */}
                    <div className='row'>
                        <div className='col-12'>
                            <div className='page-title-box d-flex  align-items-start align-items-sm-center justify-content-between gap-2'>
                                <h4 className='mb-2 mb-sm-0'>Mobile Bar</h4>
                                <div className='page-title-right'>
                                    <ol className='breadcrumb m-0'>
                                        <li className='breadcrumb-item'>
                                            <Link to='/dashboard'>Dashboard</Link>
                                        </li>
                                        <li className='breadcrumb-item active'>Mobile Bar</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-body'>
                                    {/* //* header buttons */}
                                    <div
                                        className='d-flex align-items-center justify-content-start mb-3'
                                        style={{ width: '100%' }}
                                    >
                                        <div className='header-input'>
                                            <select name='options' id='optionSelect' className='form-select' value={vendorStat} onChange={(e) => setVendorStat(e.target.value)}>
                                                <option value='3'>All</option>
                                                <option value='0'>New</option>
                                                <option value='1'>Replied</option>
                                                <option value='2'>Close</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className='table-responsive'>
                                        <table className='w-100 text-sm text-left border-collapse custom-stripe-table '>
                                            <thead className='bg-gray-200  text-center'>
                                                <tr>
                                                    <th className='px-4 py-2'>SN</th>
                                                    <th className='px-4 py-2 text-nowrap'>Vendor Name</th>
                                                    <th className='px-4 py-2 text-nowrap'>Event Date</th>
                                                    <th className='px-4 py-2 text-nowrap'>Customer Name</th>
                                                    <th className='px-4 py-2 text-nowrap'>Location</th>
                                                    <th className='px-4 py-2 text-nowrap'>Event Type</th>
                                                    <th className='px-4 py-2'>Status</th>
                                                    <th className='px-4 py-2'>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    data.length > ZERO ?
                                                        data.map((mobile, index) => (
                                                            <tr className='bg-white hover:bg-gray-100' key={mobile.id}>
                                                                <td className='px-4 py-2'>{((page - ONE) * REQUESTED_RECORD) + (index + ONE)}</td>
                                                                <td className='px-4 py-2'>{mobile.vendor?.name}</td>
                                                                <td className='px-4 py-2'>{mobile.event_date} <br /> ({mobile.event_start_time + ' to ' + mobile.event_end_time})</td>
                                                                <td className='px-4 py-2 text-nowrap'>{mobile.full_name} <br />({mobile.email})</td>
                                                                <td className='px-4 py-2'>{mobile.address}</td>

                                                                <td className='px-4 py-2'>{mobile.event_type}</td>
                                                                <td className='px-4 py-2'>
                                                                    <span className={`badge ${mobile.status == ZERO ? 'badge-primary-lighten' : mobile.status == ONE ? 'badge-secondary-lighten' : 'badge-danger-lighten'}`}>
                                                                        {mobile.status == ZERO ? 'NEW' : mobile.status == ONE ? 'REPLIED' : 'CLOSE'}
                                                                    </span>
                                                                </td>
                                                                <td className='px-4 py-2'>
                                                                    <div className='d-flex align-items-center justify-content-center'>
                                                                        <FaRegSquareCheck
                                                                            size={20}
                                                                            data-tooltip-id='approve' data-tooltip-content='Approve'
                                                                            className=' text-success vendor-icon pointer user-status me-2'
                                                                            onClick={() => showConfirmationModel({ id: mobile.id, status: ONE })}
                                                                        />
                                                                        <Tooltip id='approve' place='bottom' effect='solid' />

                                                                        <CgCloseR
                                                                            size={20}
                                                                            data-tooltip-id='reject' data-tooltip-content='Reject'
                                                                            className=' text-danger vendor-icon pointer user-status me-2'
                                                                            onClick={() => showConfirmationModel({ id: mobile.id, status: TWO })}
                                                                        />
                                                                        <Tooltip id='reject' place='bottom' effect='solid' />

                                                                        <MdRemoveRedEye
                                                                            size={20}
                                                                            data-tooltip-id='view' data-tooltip-content='View Quotation'
                                                                            className=' text-info vendor-icon pointer user-status me-2'
                                                                            onClick={() => navigate('/quotation/view-mobile-bar', {
                                                                                state: {
                                                                                    mobileBarData: mobile
                                                                                }
                                                                            })}
                                                                        />
                                                                        <Tooltip id='view' place='bottom' effect='solid' />

                                                                        <RiPencilFill
                                                                            size={20}
                                                                            data-tooltip-id='update_quotation' data-tooltip-content='Update Quotation'
                                                                            className=' text-primary vendor-icon pointer user-status me-2'
                                                                            onClick={() => navigate('/quotation/update-mobile-bar', {
                                                                                state: {
                                                                                    mobileBarData: mobile
                                                                                }
                                                                            })}
                                                                        />
                                                                        <Tooltip id='update_quotation' place='bottom' effect='solid' />

                                                                        <MdDelete
                                                                            size={20}
                                                                            data-tooltip-id='delele_quotation' data-tooltip-content='Delete Quotation'
                                                                            className=' text-danger vendor-icon pointer user-status'
                                                                            onClick={() => {
                                                                                setConfirmationModal(trueValue);
                                                                                setMobileID(mobile.id)
                                                                            }}
                                                                        />
                                                                        <Tooltip id='delele_quotation' place='bottom' effect='solid' />
                                                                    </div>

                                                                </td>
                                                            </tr>
                                                        ))
                                                        :
                                                        <tr>
                                                            <td colSpan={8} className='text-center'>No data found!</td>
                                                        </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* //* pagination */}
                                    {
                                        data.length > ZERO &&
                                        <div className='float-end'>
                                            <ReactPaginate
                                                className='pagination'
                                                onPageChange={handlePageChange}
                                                pageCount={totalPages}
                                                forcePage={(totalPages > ZERO) && (page <= totalPages) ? (page - ONE) : ZERO}
                                                previousLabel={<MdKeyboardArrowLeft className='fs-4' />}
                                                nextLabel={<MdKeyboardArrowRight className='fs-4' />}
                                                marginPagesDisplayed={TWO}
                                                pageRangeDisplayed={TWO}
                                                breakLabel={'..'}
                                                breakLinkClassName={'disabled-break'}
                                                containerClassName={'pagination-container'}
                                                activeClassName={'pagination-active-page'}
                                            />
                                        </div>
                                    }

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AllQuotation;